import { NumericFormat, PatternFormat } from "react-number-format";
import clsx from "clsx";
import { generateId } from "../../../Utils";
import FormLabel from "./FormLabel";
import InputError from "./InputError";
import { useTranslation } from "react-i18next";

const FormInput = ({
  label,
  required,
  hint,
  // A small control that belongs to the label row (e.g. "Fill max"); see FormLabel.
  labelAction,
  prefix,
  type = "text",
  id: propId,
  name,
  error,
  numberFormat,
  value,
  onChange,
  onClear,
  leadingIcon,
  trailingIcon,
  ...props
}) => {
  const { t } = useTranslation();

  const id = propId || name || generateId();

  const hasError =
    error?.messages?.length ||
    error?.message ||
    (Array.isArray(error) ? error.length : error);
  const hasPrefix = prefix && !Boolean(numberFormat?.prefix);
  const showClear = Boolean(value) && onClear;

  const inputClass = clsx(
    "block w-full rounded-md shadow-sm text-sm text-gray-900 placeholder:text-gray-400 border-0 ring-1 py-2 focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200",
    hasError
      ? "ring-red-300 focus:ring-red-500"
      : "ring-gray-300 focus:ring-blue-500",
    {
      "pl-10": hasPrefix || leadingIcon,
      "pr-10": showClear || trailingIcon,
    }
  );

  const commonProps = {
    // The label points at this id, so the control has to carry it - without it
    // the field has no accessible name at all.
    id,
    name,
    className: inputClass,
    "aria-invalid": hasError,
    "aria-errormessage": hasError ? `${id}-error` : undefined,
    "aria-describedby": Boolean(hint) ? `${id}-hint` : undefined,
    value,
    onChange,
    ...props,
  };

  return (
    <div>
      {Boolean(label) && (
        <FormLabel
          htmlFor={id}
          required={required}
          hint={hint}
          action={labelAction}
        >
          {t(label)}
        </FormLabel>
      )}
      <div className={clsx("relative rounded-md shadow-sm", Boolean(label) ? "mt-1" : "")}>
        {(hasPrefix || leadingIcon) && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {hasPrefix ? (
              <span className="text-sm text-gray-500">{prefix}</span>
            ) : (
              leadingIcon
            )}
          </div>
        )}
        {numberFormat ? (
          numberFormat.format ? (
            <PatternFormat format={numberFormat.format} {...commonProps} />
          ) : (
            <NumericFormat {...numberFormat} {...commonProps} />
          )
        ) : (
          <input type={type} {...commonProps} />
        )}
        {(showClear || trailingIcon) && (
          <div
            className={clsx(
              "absolute inset-y-0 right-0 pr-3 flex items-center",
              Boolean(trailingIcon) && "pointer-events-none"
            )}
          >
            {showClear && !props.disabled ? (
              <button
                type="button"
                disabled={props.disabled}
                onClick={onClear}
                className="text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus:text-gray-500 data-[disabled]:opacity-50"
              >
                <span className="sr-only">{t("common.clear")}</span>
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            ) : (
              trailingIcon
            )}
          </div>
        )}
      </div>
      {hasError && <InputError id={id} error={error} />}
    </div>
  );
};

export default FormInput;
